import { auth0ApiClient } from './auth0ApiClient'
import { Resource, CreateFileResourceRequest, CreateLinkResourceRequest, UpdateResourceRequest } from '@/types/resource'

class ResourceService {
  private readonly baseUrl = '/resources'

  /**
   * Get the current user's own resources, optionally filtered by title substring and/or tag
   */
  async getMyResources(filters?: { title?: string; tag?: string }): Promise<Resource[]> {
    return auth0ApiClient.get<Resource[]>(this.baseUrl, {
      params: {
        title: filters?.title || undefined,
        tag: filters?.tag || undefined
      }
    })
  }

  /**
   * Upload a file and save it to the current user's library
   */
  async createFileResource(data: CreateFileResourceRequest): Promise<Resource> {
    const formData = new FormData()
    formData.append('title', data.title)
    if (data.description) {
      formData.append('description', data.description)
    }
    if (data.tags && data.tags.length > 0) {
      formData.append('tags', data.tags.join(','))
    }
    formData.append('file', data.file)

    return auth0ApiClient.upload<Resource>(this.baseUrl, formData)
  }

  /**
   * Save an external link to the current user's library
   */
  async createLinkResource(data: CreateLinkResourceRequest): Promise<Resource> {
    return auth0ApiClient.post<Resource>(this.baseUrl, {
      title: data.title,
      description: data.description,
      tags: data.tags,
      url: data.url
    })
  }

  /**
   * Get resources shared with the current user by other owners
   */
  async getSharedWithMe(): Promise<Resource[]> {
    return auth0ApiClient.get<Resource[]>(`${this.baseUrl}/shared-with-me`)
  }

  /**
   * Update a resource's title, description, or tags
   */
  async updateResource(id: string, updates: UpdateResourceRequest): Promise<Resource> {
    return auth0ApiClient.patch<Resource>(`${this.baseUrl}/${id}`, updates)
  }

  /**
   * Delete a resource
   */
  async deleteResource(id: string): Promise<void> {
    return auth0ApiClient.delete<void>(`${this.baseUrl}/${id}`)
  }

  /**
   * Grant one or more users access to a resource (owner-only)
   */
  async shareResource(id: string, userIds: string[]): Promise<Resource> {
    return auth0ApiClient.post<Resource>(`${this.baseUrl}/${id}/share`, { userIds })
  }

  /**
   * Revoke a user's access to a resource (owner-only)
   */
  async unshareResource(id: string, userId: string): Promise<Resource> {
    return auth0ApiClient.delete<Resource>(`${this.baseUrl}/${id}/share/${userId}`)
  }
}

export const resourceService = new ResourceService()
